<script setup lang="ts">
const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const store = usePushNotificationsStore()
const mainStore = useStore()
const isLoading = ref(false)
const suppressFor30Days = ref(false)

const handleClose = () => {
  // Se o checkbox estiver marcado, suprime por 30 dias
  if (suppressFor30Days.value) {
    store.suppressOptInFor30Days()
  }
  emit('update:visible', false)
}

const handleDeclinePermanently = () => {
  store.declineNotificationsPermanently()
  mainStore.toasts.push({
    content: 'Você pode reativar as notificações nas configurações a qualquer momento.',
    color: 'info',
  })
  emit('update:visible', false)
}

const handleActivate = async () => {
  isLoading.value = true

  try {
    // Solicita permissão ao navegador
    const granted = await store.requestPermission()

    if (granted) {
      mainStore.toasts.push({
        content: 'Notificações ativadas com sucesso!',
        color: 'success',
      })

      // Fecha o modal
      handleClose()

      // Envia notificação de boas-vindas via Firebase
      setTimeout(async () => {
        try {
          await store.sendWelcomeNotification()
        } catch (error) {
          console.error('Erro ao enviar notificação de boas-vindas:', error)

          // Se for erro de token inválido, não mostra erro para o usuário
          // pois o sistema já trata automaticamente
          if (error.response?.data?.message?.includes('UNREGISTERED') ||
              error.response?.data?.message?.includes('Requested entity was not found')) {
            console.log('Token inválido detectado, será renovado automaticamente na próxima tentativa')
          }
        }
      }, 2000)
    } else {
      // Se o usuário negou a permissão
      if (store.permission === 'denied') {
        mainStore.toasts.push({
          content: 'Permissão negada. Para reativar, acesse as configurações do seu navegador.',
          color: 'warning',
        })
      } else {
        mainStore.toasts.push({
          content: 'Você pode ativar as notificações mais tarde nas configurações.',
          color: 'info',
        })
      }

      handleClose()
    }
  } catch (error) {
    console.error('Erro ao solicitar permissão:', error)
    mainStore.toasts.push({
      content: 'Erro ao ativar notificações. Tente novamente.',
      color: 'danger',
    })
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <CModal
    :visible="props.visible"
    alignment="center"
    size="lg"
    @close="handleClose"
  >
    <CModalHeader :close-button="suppressFor30Days">
      <CModalTitle>
        Receba Notificações em Tempo Real
      </CModalTitle>
    </CModalHeader>

    <CModalBody>
      <div class="text-center mb-4">
        <div class="notification-icon-wrapper mx-auto mb-3">
          <CIcon icon="cil-bell" size="3xl" class="text-primary" />
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <div class="benefit-card h-100 text-center p-3">
            <font-awesome-icon icon="fas fa-dollar-sign" class="text-success mb-2 fa-2x" />
            <h6 class="fw-bold mb-1">
              Receitas e Despesas
            </h6>
            <small class="text-muted">Transações financeiras</small>
          </div>
        </div>
        <div class="col-md-4">
          <div class="benefit-card h-100 text-center p-3">
            <font-awesome-icon icon="fas fa-clock" class="text-warning mb-2 fa-2x" />
            <h6 class="fw-bold mb-1">
              Lembretes
            </h6>
            <small class="text-muted">Vencimentos e prazos</small>
          </div>
        </div>
        <div class="col-md-4">
          <div class="benefit-card h-100 text-center p-3">
            <font-awesome-icon icon="fas fa-users" class="text-info mb-2 fa-2x" />
            <h6 class="fw-bold mb-1">
              Atividades
            </h6>
            <small class="text-muted">Ações em tempo real</small>
          </div>
        </div>
      </div>

      <CAlert color="info" class="text-start">
        <small>
          <CIcon icon="cil-info" class="me-1" />
          Você pode desativar as notificações a qualquer momento nas configurações.
        </small>
      </CAlert>

      <!-- Checkbox para não mostrar por 30 dias -->
      <div class="mt-3 py-2 px-3 bg-light rounded">
        <CFormCheck
          id="suppressFor30Days"
          v-model="suppressFor30Days"
          label="Não mostrar esta mensagem por 30 dias"
          class="text-body fw-medium"
        />
      </div>
    </CModalBody>

    <CModalFooter class="d-flex gap-3 pt-4">
      <CButton
        color="danger"
        class="flex-fill"
        @click="handleDeclinePermanently"
      >
        <font-awesome-icon icon="fas fa-ban" class="me-2" />
        Não quero receber
      </CButton>

      <CButton
        color="primary"
        :disabled="isLoading"
        class="flex-fill"
        @click="handleActivate"
      >
        <CSpinner
          v-if="isLoading"
          size="sm"
          class="me-2"
        />
        <CIcon v-else icon="cil-bell" class="me-2" />
        Ativar Notificações
      </CButton>
    </CModalFooter>
  </CModal>
</template>

<style scoped>
.notification-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--cui-primary-100), var(--cui-primary-200));
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.benefit-card {
  background: var(--cui-gray-50);
  border: 1px solid var(--cui-gray-200);
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: default;
}

.benefit-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-color: var(--cui-primary-200);
}

/* Melhora o estilo do checkbox */
:deep(.form-check-label) {
  color: var(--cui-body-color) !important;
  font-weight: 500;
  cursor: pointer;
}

:deep(.form-check-input) {
  cursor: pointer;
  scale: 1.1;
}

/* Espaçamento da modal */
.modal-footer {
  padding: 1.5rem 2rem 2rem;
  border-top: 1px solid var(--cui-gray-200);
}

.modal-body {
  padding: 1.5rem 2rem 1rem;
}

.modal-header {
  padding: 1.5rem 2rem 0.5rem;
  border-bottom: none;
}

/* Estilo dos botões */
:deep(.btn) {
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

:deep(.btn-danger) {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

:deep(.btn-danger:hover) {
  background-color: #c82333;
  border-color: #bd2130;
  color: white;
  transform: translateY(-1px);
}

:deep(.btn-primary) {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: white;
}

:deep(.btn-primary:hover) {
  background-color: #0b5ed7;
  border-color: #0a58ca;
  color: white;
  transform: translateY(-1px);
}
</style>
